import { useCallback, useMemo, useState } from 'react';
import { SizeType } from '@goldcast/player-config/src/types/asset-types';
import { ASPECT_RATIO_OPTIONS } from '../../../ClipCustomizer/Layout/constants';
import { ASPECT_RATIO_SELECTOR_OPTIONS } from './constants';
import { changeSize } from '@/stores/clip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import TooltipV2 from '@/components/TooltipV2';

const TOOLTIP_ID = 'aspect-ratio-selector-tooltip';

const PREVIEW_BASE_SIZE = 16;

function getPreviewBoxStyle(ratio: number) {
  return ratio >= 1
    ? { width: PREVIEW_BASE_SIZE, height: Math.round(PREVIEW_BASE_SIZE / ratio) }
    : { width: Math.round(PREVIEW_BASE_SIZE * ratio), height: PREVIEW_BASE_SIZE };
}

export default function AspectRatioSelector({ disabled = false }: { disabled?: boolean }) {
  const { clipId, clipData } = useClipsContext();
  const [hoveringOption, setHoveringOption] = useState<SizeType | null>(null);

  const selectedValue: SizeType = clipData.asset_metadata.size;

  const selectedIndex = useMemo(() => {
    const index = ASPECT_RATIO_SELECTOR_OPTIONS.findIndex(opt => opt.value === selectedValue);
    return index === -1 ? 0 : index;
  }, [selectedValue]);

  const onSelect = useCallback(
    (value: SizeType) => {
      changeSize(clipId, value);
    },
    [clipId]
  );

  const onMouseEnter = useCallback((value: SizeType) => {
    setHoveringOption(value);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHoveringOption(null);
  }, []);

  const tooltipContent = useMemo(() => {
    if (!hoveringOption) {
      return '';
    }
    return ASPECT_RATIO_OPTIONS[hoveringOption]?.helpText ?? '';
  }, [hoveringOption]);

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="text-xs leading-[18px]">Aspect ratio</div>
      {/* grid-cols-3 matches ASPECT_RATIO_SELECTOR_OPTIONS.length */}
      <div
        className={classnames(
          'relative grid grid-cols-3 rounded-lg bg-slate-100 p-0.5',
          disabled ? 'pointer-events-none opacity-50' : ''
        )}
        role="radiogroup"
        aria-label="Aspect ratio"
        aria-disabled={disabled}
      >
        {/* Sliding selection thumb — one column wide, translated by the selected index */}
        <span
          aria-hidden="true"
          data-testid="aspect-ratio-thumb"
          className="pointer-events-none absolute inset-y-1 left-1 rounded-md bg-white shadow-sm transition-transform duration-200 ease-out"
          style={{
            width: `calc((100% - 0.5rem) / ${ASPECT_RATIO_SELECTOR_OPTIONS.length})`,
            transform: `translateX(calc(${selectedIndex} * 100%))`
          }}
        />
        {ASPECT_RATIO_SELECTOR_OPTIONS.map(option => {
          const isSelected = option.value === selectedValue;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={option.label}
              data-tooltip-id={TOOLTIP_ID}
              onClick={() => onSelect(option.value as SizeType)}
              onMouseEnter={() => onMouseEnter(option.value as SizeType)}
              onMouseLeave={onMouseLeave}
              className={classnames(
                'z-10 flex flex-col items-center justify-between gap-1 rounded-md py-2 transition-colors',
                isSelected ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'
              )}
            >
              <div className="relative flex size-5 items-center justify-center">
                <span
                  className="inline-block rounded-[3px] border-[1.5px] border-current"
                  style={getPreviewBoxStyle(option.ratio)}
                />
              </div>
              <div className="text-sm font-medium">{option.label}</div>
            </button>
          );
        })}
      </div>
      <TooltipV2
        tooltipContent={tooltipContent}
        place="top"
        tooltipId={TOOLTIP_ID}
        size="base"
        colorTheme="light"
        className="!px-3 !py-1.5 !text-sm"
        opacity={1}
        offset={8}
      />
    </div>
  );
}
